import { AxiosResponse } from "axios";
import classNames from "classnames";
import { useFormik } from "formik";
import { FC, useCallback, useMemo, useRef } from "react";

import AsyncSearchableSelect from "components/AsyncSearchableSelect";
import Button from "components/Button";
import ImageUpload from "components/ImageUpload";
import SearchableSelect from "components/SearchableSelect";
import Textarea from "components/Textarea";
import TextInput from "components/TextInput";

import useValidation from "hooks/useValidation";

import { CompaniesApi, UsersApi } from "services/http";
import { ALLOWED_IMAGE_TYPES, ASSET_TYPE_OPTIONS, MAX_ASSET_IMAGES, MAX_IMAGE_SIZE, QUERY_PARAMS } from "utils/constants";
import { QueryHelper } from "utils/helpers";

import { AssetFormValues, Meta, UserRole } from "entities/common";
import { UserItem } from "entities/companies";
import { RootStore } from "store";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { hideModal } from "store/slices/modalSlice";

import { validationSchema } from "./validationSchema";

import styles from "../style.module.scss";

type PropTypes = {
  id?: number;
  description?: string;
  onClose?: () => void;
  initialData?: AssetFormValues;
  mode?: "create" | "edit";
  companyId?: number;
  onSubmit?: (values: unknown) => void;
};

const CreateAssetContent: FC<PropTypes> = ({ description, mode = "create", initialData, companyId, onClose, onSubmit }) => {
  const dispatch = useAppDispatch();
  const formRef = useRef<HTMLFormElement>(null);

  const authUser = useAppSelector((state: RootStore) => state.auth.user);
  const role = useMemo(() => authUser?.role, [authUser?.role]);
  const authCompanyId = useMemo(() => authUser?.company_id, [authUser?.company_id]);

  const form = useFormik<AssetFormValues>({
    initialValues: {
      name: initialData?.name || "",
      type: initialData?.type || "",
      description: initialData?.description || "",
      number: initialData?.number || "",
      managerId: initialData?.managerId || "",
      images: initialData?.images || [],
    },
    onSubmit: (values) => {
      onSubmit?.(values);
    },
    validationSchema: validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    enableReinitialize: true,
  });

  const handleTypeChange = useCallback(
    (value: string) => {
      form.setFieldValue("type", value);
    },
    [form],
  );

  const handleManagerSearch = useCallback(
    async (search: string, page: number) => {
      const currentCompanyId = companyId ?? authCompanyId;

      try {
        if (currentCompanyId && role === UserRole.ADMINISTRATOR) {
          const data = await CompaniesApi.getCompanyUsers({
            companyId: currentCompanyId,
            query: QueryHelper.getQueryString({
              [QUERY_PARAMS.SEARCH]: search || undefined,
              [QUERY_PARAMS.PAGE]: page,
              [QUERY_PARAMS.PAGE_SIZE]: 50,
            }),
          }).then((res: AxiosResponse<{ items: UserItem[] } & Meta>) => res.data);

          return {
            options: (data?.items || []).map((user) => ({ value: String(user?.id), label: user?.full_name })),
            hasMore: data?.total ? page * 50 < data.total : false,
          };
        }

        const { data } = await UsersApi.getList(
          QueryHelper.getQueryString({
            [QUERY_PARAMS.SEARCH]: search || undefined,
            [QUERY_PARAMS.PAGE]: page,
            [QUERY_PARAMS.PAGE_SIZE]: 50,
            ...(currentCompanyId ? { [QUERY_PARAMS.COMPANY_ID]: currentCompanyId } : {}),
          }),
        );

        return {
          options: (data?.items || []).map((user) => ({ value: String(user?.id), label: user?.full_name })),
          hasMore: data?.total ? page * 50 < data.total : false,
        };
      } catch {
        return { options: [], hasMore: false };
      }
    },
    [companyId, role, authCompanyId],
  );

  const handleCancel = useCallback(() => {
    onClose?.();
    dispatch(hideModal());
  }, [dispatch, onClose]);

  useValidation(formRef, form.errors);

  return (
    <div className={styles.content}>
      {!!description && <p className={styles.modalDescription}>{description}</p>}

      <form ref={formRef} onSubmit={form.handleSubmit} className={styles.form}>
        <fieldset className={classNames(styles.fieldset, styles.basicInfo)}>
          <p className={styles.fieldsetTitle}>Basic information</p>

          <div className={styles.fieldsetFields}>
            <TextInput
              isRequired
              name="name"
              label="Asset name"
              placeholder="Asset name"
              maxLength={100}
              value={form.values.name}
              error={form.errors.name}
              onChange={form.handleChange}
            />

            <SearchableSelect
              isRequired
              options={ASSET_TYPE_OPTIONS}
              label="Asset type"
              placeholder="Select type"
              value={form.values.type}
              error={form.errors.type}
              onChange={handleTypeChange}
            />

            <Textarea
              name="description"
              label="Description"
              placeholder="Brief description of the asset..."
              maxLength={500}
              onChange={form.handleChange}
              value={form.values.description}
              error={form.errors.description}
            />
          </div>
        </fieldset>

        <fieldset className={styles.fieldset}>
          <p className={styles.fieldsetTitle}>Responsible person</p>

          <AsyncSearchableSelect
            isRequired
            label="Asset manager"
            placeholder="Select manager"
            value={form.values.managerId}
            onChange={(value) => form.setFieldValue("managerId", value)}
            onSearch={handleManagerSearch}
            error={form.errors.managerId}
          />

          <div className={styles.inputHint}>
            <span>!</span>
            <span>This is the person who will receive notifications if incidents occur in the asset</span>
          </div>
        </fieldset>

        <fieldset className={styles.fieldset}>
          <p className={styles.fieldsetTitle}>Asset image</p>

          <ImageUpload
            images={form.values.images}
            onChange={(images) => form.setFieldValue("images", images)}
            maxFiles={MAX_ASSET_IMAGES}
            maxSize={MAX_IMAGE_SIZE}
            allowedTypes={ALLOWED_IMAGE_TYPES}
          />
        </fieldset>

        <div className={styles.buttons}>
          <Button type="button" variant="secondary" onClick={handleCancel}>
            Cancel
          </Button>

          <Button type="submit" variant="primary">
            {mode === "create" ? "Create" : "Update"}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default CreateAssetContent;
